import { Injectable, inject, resource, DestroyRef, effect, computed } from "@angular/core";
import { Observable, Subject, Subscriber, firstValueFrom, filter, take, combineLatest } from "rxjs";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import type {
    IDashboardModel,
    ICategories,
    ICategoriesApiResponse,
    ICategoryCreateResponse,
    ICategoryData,
    ICategoryDeleteResponse,
    ICategoryUpdateResponse,
    ISaveCategoriesData,
    IT1Tags,
} from "@app/views-interfaces";
import { DashboardsService } from "@shared/dashboards-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import type { DashboardId } from "@interfaces";
import { AuthService } from "@shared/auth";
import { HttpService } from "@classes";

type CategoriesByDashboardId = Map<DashboardId, ICategories[]>;

@Injectable({ providedIn: "root" })
export class CategoriesService extends HttpService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    protected path = "categories/";
    protected urlKey = "CategoriesService_URL";
    protected versionKey = "CategoriesService_Version";

    protected getUrl(path = this.path, dashboardId = this.dashboardsService.getCurrentDashboardIdOrThrow()): string {
        return super.getUrl(path) + dashboardId + "/";
    }

    private categoriesLoadedEmitter = new Subject();
    public categoriesLoadedEvent$ = this.categoriesLoadedEmitter.asObservable();

    private categoriesCache: CategoriesByDashboardId = new Map();
    public isSaving = 0;
    private inError = false;
    private isReloading = true;

    private categoriesResource = resource<ICategories[], DashboardId>({
        defaultValue: [],
        params: () => this.dashboardsService.currentId(),
        loader: async ({ params: dashboardId }) => {
            if (!dashboardId) return [];

            if (!this.isReloading && this.categoriesCache.has(dashboardId)) return this.categoriesCache.get(dashboardId) ?? [];

            const categories = await this.loadCategories();
            this.categoriesCache.set(dashboardId, categories);

            return categories;
        },
    });
    private categoriesResourceStatus$ = toObservable(this.categoriesResource.status);
    public readonly currentCategories = this.categoriesResource.value.asReadonly();
    private readonly currentCategories$ = toObservable(this.categoriesResource.value);

    private readonly categoryById = computed(() => {
        const categories = this.currentCategories();
        const map = new Map<string, ICategories>();
        for (const category of categories) {
            if (!category?.Id) continue;
            map.set(category.Id, category);
        }
        return map;
    });

    constructor() {
        super();

        this.authService.sessionData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());

        effect(() => {
            const categories = this.currentCategories();
            this.categoriesLoadedEmitter.next(categories);
        });
    }

    public reload(): void {
        this.isReloading = true;
        this.categoriesResource.reload();
    }

    private async loadCategories(): Promise<ICategories[]> {
        try {
            const result = await firstValueFrom(this.get<ICategoriesApiResponse>());
            if (result.Code == 200) return result.Categories;
        } catch (error) {
            console.error(error);
        }

        this.responseErrorHandlerService.showPopup({
            typeLayout: "ERROR",
            messageCode: "defaultError",
        });
        return [];
    }

    public setCategories(categories: ICategories[]): void {
        const dashboardId = this.dashboardsService.currentId();
        this.categoriesCache.set(dashboardId, categories);
        this.calculateCategoryCache = {};
    }

    public async getCategoriesList(): Promise<ICategories[]> {
        await this.waitForCategories();
        return this.currentCategories() ?? [];
    }

    // Avoid using it all the times because can create problems. Only to be used in a route guard
    private async waitForCategories(): Promise<void> {
        await firstValueFrom(
            combineLatest([this.categoriesResourceStatus$, this.currentCategories$]).pipe(
                filter(([status, categories]) => status !== "loading" && status !== "reloading" && categories !== undefined && categories !== null),
                take(1),
            ),
        );
    }

    public getCategory(id: string): ICategories | null {
        return this.categoryById()?.get(id) ?? null;
    }

    public getCategories(): readonly ICategories[] {
        return this.currentCategories();
    }

    public getCardCategory(card: { categoryId: string | null; tags: string[] }, dashboard: IDashboardModel): ICategories | null {
        if (card.categoryId === null) return null;

        const category = this.getCategory(card.categoryId);
        if (category && card.categoryId !== "Unassigned") return category;

        return this.calculateCategory(dashboard, card.tags);
    }

    public addCategory(categoryData: ICategoryData): Observable<ICategoryCreateResponse> {
        return this.post<ICategoryCreateResponse>("", categoryData);
    }

    public updateCategory(categoryData: ICategoryData | ISaveCategoriesData["updateCategory"]): Observable<ICategoryUpdateResponse> {
        return this.put<ICategoryUpdateResponse>("", categoryData);
    }

    public deleteCategory(categoryId: string): Observable<ICategoryDeleteResponse> {
        return this.delete<ICategoryDeleteResponse>(categoryId);
    }

    public fillTagsByIdForCategories(categories: ICategories[], tags: IT1Tags[]): ICategories[] {
        const categoriesWhitTags = categories.map((category: ICategories) => {
            if (!category?.Tags?.length) return category;

            category.Tags.forEach((_, i) => {
                tags.forEach((element) => {
                    if (element.Id === category?.Tags?.[i]) category.Tags[i] = structuredClone(element);
                });
            });
            return category;
        });
        return categoriesWhitTags;
    }

    public sortByOrder(a: ICategories, b: ICategories): number {
        if (a.Order == null && b.Order == null) return 0;
        else if (b.Order == null) return 1;
        else if (a.Order == null) return -1;
        else return a.Order - b.Order;
    }

    public saveCategories(fileCategoryModelCategories: ICategories[]): Observable<void> {
        return new Observable<void>((observer) => {
            this.isSaving = 0;
            this.inError = false;
            const preparedData: ISaveCategoriesData = {
                addCategory: [],
                updateCategory: {},
            };
            fileCategoryModelCategories.forEach((category, Order) => {
                if (category.tempId) {
                    preparedData.addCategory.push({
                        Name: category.Name,
                        Tags: category?.Tags?.filter((tag) => tag?.Id).map((tag) => tag.Id as never) ?? [],
                        Order: Order,
                    } as never);
                } else if (category?.Id) {
                    preparedData.updateCategory[category.Id] = {
                        Name: category.Name,
                        Tags: category?.Tags?.filter((tag) => tag?.Id).map((tag) => tag.Id as never) ?? [],
                        Order: Order,
                    };
                }
            });
            if (preparedData.addCategory.length > 0) {
                preparedData.addCategory.forEach((newCategory: ICategoryData) => {
                    this.isSaving = ++this.isSaving;
                    this.addCategory(newCategory)
                        .pipe(take(1))
                        .subscribe({
                            next: (response: ICategoryCreateResponse) => {
                                const order = response.Category.Order;
                                if (order != null && fileCategoryModelCategories[order]) {
                                    fileCategoryModelCategories[order].Id = response.Category.Id;
                                    delete fileCategoryModelCategories[order].tempId;
                                }
                                this.isSaving = --this.isSaving;
                                this.returnObservableIfNeeded(observer);
                            },
                            error: () => {
                                this.isSaving = --this.isSaving;
                                this.inError = true;
                                this.returnObservableIfNeeded(observer);
                            },
                        });
                });
            }

            if (Object.getOwnPropertyNames(preparedData.updateCategory).length > 0) {
                this.isSaving = ++this.isSaving;
                this.updateCategory(preparedData.updateCategory)
                    .pipe(take(1))
                    .subscribe({
                        next: () => {
                            this.isSaving = --this.isSaving;
                            this.returnObservableIfNeeded(observer);
                        },
                        error: () => {
                            this.isSaving = --this.isSaving;
                            this.inError = true;
                            this.returnObservableIfNeeded(observer);
                        },
                    });
            }

            this.returnObservableIfNeeded(observer);
        });
    }

    public returnObservableIfNeeded(observer: Subscriber<void>) {
        if (this.isSaving === 0) {
            if (this.inError) {
                observer.error();
                observer.complete();
            } else {
                observer.next();
                observer.complete();
            }
        }
    }

    private calculateCategoryCache: Record<string, ICategories | null> = {};
    public calculateCategoryWithCategories(
        currentCategories: ICategories[],
        currentDashboardAssignCategoryNoTags: boolean | string,
        tags: string[],
    ): ICategories | null {
        if (Object.hasOwn(this.calculateCategoryCache, tags.length + tags.join(""))) {
            return this.calculateCategoryCache[tags.length + tags.join("")];
        }
        let matchingCategory: ICategories | null = null;
        let matchingCategoryOrder: number | null = null;
        if (tags.length > 0) {
            // Find the matching category with lowest Order value
            tags.forEach((tag) => {
                const category = currentCategories.find((cat) => cat.Tags?.includes(tag));

                if (category) {
                    if (matchingCategoryOrder === null || (category.Order != null && category.Order < matchingCategoryOrder)) {
                        matchingCategory = category;
                        matchingCategoryOrder = category.Order ?? null;
                    }
                }
            });
        }
        if (!matchingCategory) {
            // If no tags are supplied or tags not in a category, we have a setting to allow returning the rightmost category
            if (typeof currentDashboardAssignCategoryNoTags === "boolean") {
                if (currentDashboardAssignCategoryNoTags) {
                    // Find the matching category with highest Order value
                    currentCategories.forEach((category) => {
                        if (matchingCategoryOrder === null || (category.Order != null && category.Order > matchingCategoryOrder)) {
                            matchingCategory = category;
                            matchingCategoryOrder = category.Order ?? null;
                        }
                    });
                }
            } else {
                if (currentDashboardAssignCategoryNoTags) {
                    // Find the matching category
                    matchingCategory = currentCategories.find((category) => category.Id == currentDashboardAssignCategoryNoTags) ?? matchingCategory;
                }
            }
        }

        this.calculateCategoryCache[tags.length + tags.join("")] = matchingCategory;
        return matchingCategory;
    }

    public calculateCategory(currentDashboard: IDashboardModel, tags: string[]): ICategories | null {
        const assignCategoryNoTags =
            currentDashboard.TaxYear && currentDashboard.TaxYear < 2025 ? currentDashboard.AssignCategoryNoTags : currentDashboard.AssignCategoryId;
        return this.calculateCategoryWithCategories(this.currentCategories(), assignCategoryNoTags, tags);
    }
}
